/**
 * useViewfinderExposure — the PRE-shot, correct-before-shooting cue for the
 * guided capture viewfinder (INTAKE-08 §A1).
 *
 * Honesty contract (the part that matters more than the maths):
 *   • The ONLY thing cheaply + honestly detectable client-side without a face/
 *     document detector in the bundle (INTAKE-09 §3.2 bans one) is EXPOSURE —
 *     mean luma against the same INTERIM band the post-shot gate uses. So this
 *     hook reports 'too-dark' / 'too-bright' / null, and NOTHING else.
 *   • null means "no claim" — the guide frame stays NEUTRAL. It is NEVER a
 *     "looks good"/ready signal: claiming readiness from brightness alone would
 *     fabricate a cue (INTAKE-08 §A1: "never fake a 'looks good' cue"). The
 *     post-shot verdict gate stays the honest arbiter.
 *   • Advisory only — it never disables the shutter and never blocks anything.
 *
 * Cost discipline (low-end India-mobile budget, INTAKE-08 §1.3): samples a
 * 64px-wide grayscale downscale at most ~2.5×/s (≤ the spec's 4/s ceiling, and
 * hysteresis of one sample so the frame never strobes at a band edge). No
 * pixels leave this function; nothing is logged or posted (PII discipline).
 */

import { useEffect, useRef, useState } from 'react';
import { LUMA_MAX, LUMA_MIN } from './imageQuality';

export type ExposureCue = 'too-dark' | 'too-bright' | null;

const SAMPLE_MS = 400; // ≤ 4 samples/s per INTAKE-08 §A1 (we run 2.5/s)
const SAMPLE_WIDTH = 64;

/**
 * @param getVideo stable getter for the live viewfinder <video> (null when not live)
 * @param enabled  true only while the live viewfinder is on screen
 */
export function useViewfinderExposure(
  getVideo: () => HTMLVideoElement | null,
  enabled: boolean,
): ExposureCue {
  const [cue, setCue] = useState<ExposureCue>(null);
  /** Hysteresis: only surface a cue seen on two consecutive samples. */
  const lastRaw = useRef<ExposureCue>(null);

  useEffect(() => {
    if (!enabled) {
      setCue(null);
      lastRaw.current = null;
      return undefined;
    }
    const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
    const ctx = canvas?.getContext('2d', { willReadFrequently: true }) ?? null;
    if (!canvas || !ctx) return undefined; // cannot sample — no claim, neutral frame

    const timer = window.setInterval(() => {
      const el = getVideo();
      if (!el || el.readyState < 2 || !el.videoWidth || !el.videoHeight) {
        lastRaw.current = null;
        setCue(null);
        return;
      }
      try {
        const scale = SAMPLE_WIDTH / el.videoWidth;
        const w = SAMPLE_WIDTH;
        const h = Math.max(2, Math.round(el.videoHeight * scale));
        canvas.width = w;
        canvas.height = h;
        ctx.drawImage(el, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h).data;
        let sum = 0;
        const n = w * h;
        for (let i = 0; i < n; i++) {
          const o = i * 4;
          sum += 0.299 * (data[o] ?? 0) + 0.587 * (data[o + 1] ?? 0) + 0.114 * (data[o + 2] ?? 0);
        }
        const mean = sum / n;
        const raw: ExposureCue = mean < LUMA_MIN ? 'too-dark' : mean > LUMA_MAX ? 'too-bright' : null;
        // Two consecutive identical reads before the UI moves (no strobing).
        setCue(raw === lastRaw.current ? raw : null);
        lastRaw.current = raw;
      } catch {
        // Sampling failed (e.g. tainted canvas) — no claim, never a crash.
        lastRaw.current = null;
        setCue(null);
      }
    }, SAMPLE_MS);
    return () => window.clearInterval(timer);
  }, [enabled, getVideo]);

  return cue;
}
